// The job's estimate, Blueprint (DEC-019, FLO-31): ui-web's EstimateBuilder over the job page, at its
// Build step, from the same draft the office's editor keeps (job-model.ts). Its options side by side,
// each with its title, tier and what it includes, its lines (quantity, price and, for a price off the
// book, why), Copy and Remove; the price book beside them (pricebook.listItems as typed, by kind) adding
// to the option its head names, or a line written in; a new estimate starts from the job's option
// templates (optionTemplates.list) or blank. Save the draft is estimates.save; Send to the customer
// saves, then opens the send. What still stops a save is said in the foot.
import { useEffect, useState } from 'react';
import type { JobDetail } from '../review/review-model.ts';
import { EstimateBuilder, type EditorLine } from '@flowology/ui-web/job';
import { Link, money, SelectField, TextField } from '@flowology/ui-web/fl';
import { client } from '../api.ts';
import { errorText } from '../books/ui.tsx';
import { useSaveKey } from '../shared/SaveButton.tsx';
import { draftFromTemplate, type OptionTemplate } from '../settings/option-templates-model.ts';
import { pictureSrc } from '../settings/pricebook-book-model.ts';
import {
  blankOption,
  canAddOption,
  draftOf,
  draftProblems,
  freeLine,
  itemLine,
  newDraft,
  saveInput,
  takeTodaysPrice,
  withQuantity,
  type Draft,
  type DraftOption,
  type Estimate,
} from './job-model.ts';
import {
  builderOptionOf,
  copyOf,
  findInput,
  KINDS,
  pickerItemOf,
  TIER_OPTIONS,
  withLine,
  withOption,
  type FoundItem,
} from './estimate-build-model.ts';

const MAX_LINES = 60;

export function EstimateBuild({
  job,
  start,
  onClose,
  onSaved,
  onSend,
}: {
  job: JobDetail;
  start: Estimate | 'new';
  onClose: () => void;
  onSaved: (e: Estimate, isNew: boolean) => void;
  /** Saved, to go to the customer: the send opens on it. */
  onSend: (e: Estimate) => void;
}) {
  const isNew = start === 'new';
  const [d, setD] = useState<Draft>(() =>
    isNew ? newDraft(job.id, job.summary.slice(0, 200)) : draftOf(start),
  );
  const [busy, setBusy] = useState<'save' | 'send' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const save = useSaveKey(JSON.stringify(d));
  const plans = job.memberPlans ?? [];
  const first = job.customerName.split(' ')[0] || 'the customer';

  // the price book as typed, by kind; the pictures of what was added, kept for its lines
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState('all');
  const [found, setFound] = useState<FoundItem[]>([]);
  const [pictures, setPictures] = useState<Record<string, EditorLine['picture']>>({});
  useEffect(() => {
    let current = true;
    const t = setTimeout(() => {
      client
        .call<{ items: FoundItem[] }>('pricebook.listItems', findInput(query, kind))
        .then((r) => current && setFound(r.items))
        .catch(() => current && setFound([]));
    }, 200);
    return () => {
      current = false;
      clearTimeout(t);
    };
  }, [query, kind]);

  // a new estimate can start from a saved option set for this job, priced today
  const [templates, setTemplates] = useState<OptionTemplate[]>([]);
  const [chosen, setChosen] = useState<string | undefined>(undefined);
  const [fromTemplate, setFromTemplate] = useState<string | null>(null);
  // the option whose name, tier and words are open to change (closed, the column reads as designed)
  const [naming, setNaming] = useState<string | null>(null);
  useEffect(() => {
    if (!isNew) return;
    client
      .call<{ templates: OptionTemplate[] }>('optionTemplates.list', { jobId: job.id })
      .then((r) => setTemplates(r.templates))
      .catch(() => setTemplates([]));
  }, [isNew, job.id]);
  const choose = (id: string) => {
    setChosen(id);
    if (id === 'blank') {
      setFromTemplate(null);
      return setD((x) => ({ ...x, options: [blankOption(0)] }));
    }
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    const { options, skipped } = draftFromTemplate(t);
    if (!options.length)
      return setFromTemplate(`${t.name}: the price book no longer offers any of its items.`);
    setD((x) => ({ ...x, options }));
    setFromTemplate(
      `Started from ${t.name}, at today’s prices.${
        skipped
          ? ` ${skipped === 1 ? '1 line was' : `${skipped} lines were`} left out: the price book no longer offers ${skipped === 1 ? 'it' : 'them'}.`
          : ''
      }`,
    );
  };

  const problems = draftProblems(d);
  const run = async (then: 'save' | 'send') => {
    setTried(true);
    if (problems.length || busy) return;
    setBusy(then);
    setError(null);
    try {
      const e = await client.call<Estimate>('estimates.save', saveInput(d), { idempotencyKey: save.key });
      if (then === 'send') onSend(e);
      else onSaved(e, isNew);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  };

  const addItem = (optionId: string, id: string) => {
    const item = found.find((i) => i.id === id);
    if (!item) return;
    setPictures((p) => ({
      ...p,
      [id]: pictureSrc({ art: item.art, picture: item.picture ?? null }, 'drawing'),
    }));
    setD((x) =>
      withOption(x, optionId, (o) =>
        o.lines.length < MAX_LINES ? { ...o, lines: [...o.lines, itemLine(item, plans)] } : o,
      ),
    );
  };
  const option = (o: DraftOption) => (f: (o: DraftOption) => DraftOption) =>
    setD((x) => withOption(x, o.optionId, f));

  const options = d.options.map((o, n) => {
    const b = builderOptionOf(o, n, pictures);
    const set = option(o);
    return {
      ...b,
      lines: b.lines.map((line) => {
        const l = o.lines.find((x) => x.lineId === line.id)!;
        const free = l.pricebookItemId === null;
        const change = (f: Parameters<typeof withLine>[3]) =>
          setD((x) => withLine(x, o.optionId, l.lineId, f));
        return {
          ...line,
          below: (
            <>
              {free ? (
                <>
                  <TextField
                    label="What it is"
                    value={l.description}
                    onChange={(v) => change((y) => ({ ...y, description: v }))}
                  />
                  <TextField
                    label="Our cost (optional)"
                    value={l.cost}
                    onChange={(v) => change((y) => ({ ...y, cost: v }))}
                  />
                  <TextField
                    label="Why (a line not from the price book needs a reason)"
                    value={l.reason}
                    onChange={(v) => change((y) => ({ ...y, reason: v }))}
                  />
                </>
              ) : null}
              {!free && l.bookNowCents != null ? (
                // the customer saw this price: it stays unless someone takes today's on purpose
                <span>
                  Book now says {money(l.bookNowCents)} ·{' '}
                  <Link onPress={() => change(takeTodaysPrice)}>Use today’s price</Link>
                </span>
              ) : null}
              <Link
                onPress={() => set((y) => ({ ...y, lines: y.lines.filter((z) => z.lineId !== l.lineId) }))}
              >
                Remove this line
              </Link>
            </>
          ),
        };
      }),
      fields:
        naming === o.optionId ? (
          <>
            <TextField
              label="Its title"
              value={o.title}
              onChange={(v) => set((y) => ({ ...y, title: v.slice(0, 120) }))}
            />
            <SelectField
              label="Tier"
              value={o.tier}
              onChange={(v) => set((y) => ({ ...y, tier: v as DraftOption['tier'] }))}
              options={TIER_OPTIONS}
            />
            <TextField
              label="What it includes (optional)"
              multiline
              value={o.summary}
              onChange={(v) => set((y) => ({ ...y, summary: v.slice(0, 2000) }))}
            />
          </>
        ) : undefined,
      actions: (
        <>
          <Link onPress={() => setNaming((x) => (x === o.optionId ? null : o.optionId))}>
            {naming === o.optionId ? 'Done naming it' : 'Name it'}
          </Link>
          {canAddOption(d) ? (
            <Link
              onPress={() =>
                setD((x) => {
                  const c = copyOf(x, o.optionId);
                  return c ? { ...x, options: [...x.options, c] } : x;
                })
              }
            >
              Copy this option
            </Link>
          ) : null}
          {d.options.length > 1 ? (
            <Link
              onPress={() =>
                setD((x) => ({ ...x, options: x.options.filter((y) => y.optionId !== o.optionId) }))
              }
            >
              Remove this option
            </Link>
          ) : null}
        </>
      ),
    };
  });

  // the option the book adds to, for "Write a line in instead"
  const [focus, setFocus] = useState<string | null>(null);
  const target = d.options.find((o) => o.optionId === focus) ?? d.options[0];

  return (
    <EstimateBuilder
      step="build"
      // Present and Sign stay the job page's (Send, then the customer's link): Build is the one step here
      steps={['build']}
      onClose={onClose}
      view={{
        job: `Job ${job.number}`,
        number: isNew ? 'New estimate' : `Estimate ${start.number}`,
        name: d.title.trim() || 'A new estimate',
        customer: job.customerName,
        stateLine: isNew ? 'a new estimate, not saved yet' : 'a draft: saving it again replaces it',
        head: { summary: d.title },
        options,
        picker: {
          groups: KINDS,
          items: found.map((i) => pickerItemOf(i, plans)),
          onQuery: setQuery,
          onGroup: setKind,
          find: {
            placeholder: 'Find: name or code',
            foot: target ? (
              <Link
                onPress={() =>
                  setD((x) =>
                    withOption(x, target.optionId, (o) =>
                      o.lines.length < MAX_LINES ? { ...o, lines: [...o.lines, freeLine()] } : o,
                    ),
                  )
                }
              >
                Write a line in instead
              </Link>
            ) : undefined,
          },
        },
        ...(isNew && templates.length
          ? {
              startFrom: {
                sets: templates.map((t) => ({
                  id: t.id,
                  name: t.name,
                  note: `${t.options.length} option${t.options.length === 1 ? '' : 's'}, at today’s prices`,
                  pictures: [],
                })),
                chosen,
                onChoose: choose,
                ...(fromTemplate ? { note: fromTemplate } : {}),
              },
            }
          : {}),
        canPrice: true,
        presentLabel: `Send to ${first}`,
        history: [],
        sign: { option: '', total: '', deposit: '' },
        showMargin: false,
      }}
      act={{
        onQty: (o, l, q) => {
          setFocus(o);
          setD((x) => withLine(x, o, l, (y) => withQuantity(y, q)));
        },
        onPrice: (o, l, p) => {
          setFocus(o);
          setD((x) => withLine(x, o, l, (y) => ({ ...y, price: p })));
        },
        onReason: (o, l, r) => setD((x) => withLine(x, o, l, (y) => ({ ...y, reason: r }))),
        onAdd: (o, id) => {
          setFocus(o);
          addItem(o, id);
        },
        onSummary: (t) => setD((x) => ({ ...x, title: t.slice(0, 200) })),
        onSave: () => void run('save'),
        saveLabel: busy === 'save' ? 'Saving…' : 'Save the draft',
        onPrimary: () => void run('send'),
        isBusy: busy === 'send',
        footLinks: canAddOption(d) ? (
          <Link
            onPress={() => setD((x) => ({ ...x, options: [...x.options, blankOption(x.options.length)] }))}
          >
            Add an option
          </Link>
        ) : null,
        onFocus: setFocus,
        footNote:
          tried && problems.length
            ? { text: `Before it can be saved: ${problems.join(' ')}`, isBad: true }
            : error
              ? { text: error, isBad: true }
              : undefined,
      }}
    />
  );
}
